"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Lock, ShieldCheck } from "lucide-react";

const REASONS = [
  { label: { en: "On the house", es: "Cortesía de la casa" } },
  { label: { en: "Friend of the house", es: "Amigo de la casa" }, selected: true },
  { label: { en: "Broken glass", es: "Vaso roto" } },
  { label: { en: "Bartender error", es: "Error del bartender" } },
  { label: { en: "Other", es: "Otro" } },
];

export default function LiveBarTabsCompScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      <div
        className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.20) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Comps & voids", "Comps y voids")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("Comp? ", "¿Comp? ")}
            <span className="text-white/30">{t("PIN required.", "PIN requerido.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "Every comp logged. Every void logged. Every reason picked from a list. End-of-night you know exactly what walked, why it walked, and who let it walk.",
              "Cada comp registrado. Cada void registrado. Cada razón elegida de una lista. Al cerrar la noche sabes exactamente qué se fue, por qué, y quién lo dejó ir."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[28px] p-7 md:p-8 max-w-[640px] mx-auto"
        >
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "rgba(244,114,182,0.2)" }}
            >
              <Lock className="w-5 h-5" style={{ color: "#F472B6" }} />
            </div>
            <div>
              <div className="text-[10px] font-black tracking-widest uppercase text-white/45">
                {t("Comp request", "Solicitud de comp")}
              </div>
              <div className="text-[16px] font-black text-white">
                {t("Mezcal Negroni · $18", "Mezcal Negroni · $18")}
              </div>
            </div>
          </div>

          {/* PIN entry */}
          <div className="mb-6 text-center">
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/45 mb-3">
              {t("Manager PIN", "PIN del manager")}
            </div>
            <div className="flex items-center justify-center gap-2">
              {[true, true, true, true].map((filled, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0.6, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.3, delay: 0.3 + 0.12 * i }}
                  className="w-12 h-12 rounded-[14px] border border-white/15 flex items-center justify-center"
                  style={{
                    backgroundColor: filled ? "rgba(244,114,182,0.12)" : "rgba(255,255,255,0.04)",
                  }}
                >
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{
                      backgroundColor: filled ? "#F472B6" : "rgba(255,255,255,0.15)",
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Reason picker */}
          <div className="mb-6">
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/45 mb-3">
              {t("Reason", "Razón")}
            </div>
            <div className="space-y-1.5">
              {REASONS.map((r, i) => (
                <motion.div
                  key={r.label.en}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: 0.8 + 0.08 * i }}
                  className={`flex items-center justify-between rounded-[10px] px-3 py-2.5 border ${
                    r.selected ? "" : "bg-white/[0.03] border-white/5"
                  }`}
                  style={
                    r.selected
                      ? {
                          backgroundColor: "rgba(244,114,182,0.12)",
                          borderColor: "rgba(244,114,182,0.35)",
                        }
                      : undefined
                  }
                >
                  <span
                    className={`text-[13px] ${r.selected ? "font-black" : "font-medium text-white/65"}`}
                    style={r.selected ? { color: "#F472B6" } : undefined}
                  >
                    {t(r.label.en, r.label.es)}
                  </span>
                  {r.selected && (
                    <ShieldCheck className="w-4 h-4" style={{ color: "#F472B6" }} />
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Audit message */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="text-center text-[11px] font-medium text-white/45 italic"
          >
            {t(
              "Logged: Maria L. comped Mezcal Negroni — \"Friend of the house\" — Sat 1:32 AM.",
              "Registrado: Maria L. comp Mezcal Negroni — \"Amigo de la casa\" — Sáb 1:32 AM."
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
